import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Plus, Tag, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';
import { getApiUrl, getImageUrl } from '@/utils/api';

const API = getApiUrl();

const CheckoutUpsell = ({ cart, onAddToCart, sessionId }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState({
    enabled: true,
    discount_percent: 15,
    title: "APROVEITE! {discount}% OFF NESSES PRODUTOS",
    subtitle: "Adicione mais produtos e economize no frete!",
    badge_text: "-{discount}%",
    max_products: 4
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  useEffect(() => {
    if (config.enabled) {
      fetchUpsellProducts();
    }
  }, [cart, config.enabled, config.max_products]);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/upsell-config`);
      setConfig(response.data);
    } catch (error) {
      console.error('Error fetching upsell config:', error);
    }
  };

  const fetchUpsellProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?limit=20`);
      const allProducts = response.data;
      
      // Filter out products already in cart and select random ones
      const cartProductIds = cart.map(item => item.product_id);
      const availableProducts = allProducts.filter(
        product => !cartProductIds.includes(product.id)
      );
      
      // Shuffle and take max_products
      const shuffled = availableProducts.sort(() => Math.random() - 0.5);
      setProducts(shuffled.slice(0, config.max_products));
    } catch (error) {
      console.error('Error fetching upsell products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (product, size = 'M') => {
    try {
      await axios.post(`${API}/cart`, {
        product_id: product.id,
        size: size,
        quantity: 1,
        session_id: sessionId
      });
      
      toast.success(`${product.name} adicionado ao carrinho!`);
      
      if (onAddToCart) {
        onAddToCart();
      }
      
      // Remove from upsell list
      setProducts(prev => prev.filter(p => p.id !== product.id));
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Erro ao adicionar produto');
    }
  };

  // Calculate discount price based on config
  const getDiscountPrice = (price) => {
    return price * (1 - config.discount_percent / 100);
  };

  // Replace {discount} placeholder with actual value
  const formatText = (text) => {
    return text.replace('{discount}', config.discount_percent);
  };

  if (!config.enabled || loading || products.length === 0) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/30 p-4 lg:p-6 mb-6"
      data-testid="checkout-upsell"
    >
      <div className="flex items-center gap-2 mb-4">
        <Tag className="h-5 w-5 text-primary" />
        <h3 className="font-heading text-lg font-bold text-white">
          {formatText(config.title)}
        </h3>
      </div>
      
      <p className="text-zinc-400 text-sm mb-4">
        {config.subtitle}
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {products.map((product) => (
          <motion.div
            key={product.id}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-zinc-900/80 border border-white/10 p-3 group hover:border-primary/50 transition-all"
          >
            <div className="relative mb-2">
              <img
                src={getImageUrl(product.image)}
                alt={product.name}
                className="w-full aspect-square object-cover"
              />
              <div className="absolute top-1 right-1 bg-primary text-black text-[10px] font-bold px-1.5 py-0.5">
                {formatText(config.badge_text)}
              </div>
            </div>
            
            <h4 className="font-heading text-xs font-bold text-white truncate mb-1">
              {product.name}
            </h4>
            
            <div className="flex items-center gap-2 mb-2">
              <span className="text-zinc-500 text-xs line-through">
                R$ {product.price.toFixed(2)}
              </span>
              <span className="text-primary font-mono text-sm font-bold">
                R$ {getDiscountPrice(product.price).toFixed(2)}
              </span>
            </div>
            
            <button
              onClick={() => handleAddToCart(product)}
              className="w-full bg-primary/20 hover:bg-primary text-primary hover:text-black text-xs font-bold py-2 transition-all flex items-center justify-center gap-1"
            >
              <Plus className="h-3 w-3" />
              ADICIONAR
            </button>
          </motion.div>
        ))}
      </div>
      
      <div className="mt-4 text-center">
        <p className="text-xs text-zinc-500">
          <ShoppingBag className="h-3 w-3 inline mr-1" />
          Desconto aplicado automaticamente ao adicionar
        </p>
      </div>
    </motion.div>
  );
};

export default CheckoutUpsell;
